import { observer } from "mobx-react-lite";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useBreakpoint } from "use-breakpoint";
import { useStore } from "../../root-store-context";
import { Button, Card, Checkbox, Descriptions, Flex, Spin, Modal, Form, Input, Select, message, Space } from "antd";
import { MailOutlined, MenuOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { CommunityAdditionalContact, ContactsAndLinksItem } from "../../api/Association/AssociationTypes";
import { validateContactDetail } from "../../helpers/validators";
import DeleteIcon from "../../images/icons/delete.svg";
import EditIcon from "../../images/icons/edit.svg";

const { Option } = Select;

const ContactsAndLinks = observer((): ReactElement => {
  const BREAKPOINTS = { mobile: 0, desktop: 768 };
  const { breakpoint } = useBreakpoint(BREAKPOINTS, "mobile");
  const { contactsAndLinksStore, applicationStore } = useStore();
  const [contactsAndLinks, setContactsAndLinks] = useState<ContactsAndLinksItem>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const [editList, setEditList] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: number]: { header?: string; contactDetail?: string; contactType?: string } }>({});

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const handleSort = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;

    const listCopy = [...editList];

    const draggedItem = listCopy[dragItem.current];
    listCopy.splice(dragItem.current, 1);
    listCopy.splice(dragOverItem.current, 0, draggedItem);

    const updatedList = listCopy.map((item, idx) => ({
      ...item,
      sortOrder: idx + 1, // sortOrder starts from 1
    }));

    setEditList(updatedList);

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleChange = (index: number, key: string, value: string) => {
    const newList = [...editList];
    (newList[index] as any)[key] = value;
    setEditList(newList);
  };

  const [form] = Form.useForm();

  // Initial load
  useEffect(() => {
    contactsAndLinksStore.getContactsAndLinks().then((res) => {
      setContactsAndLinks(res);
      setIsLoading(false);
    });
  }, []);

  // ---------------------- RENDER HELPERS ----------------------
  const renderText = (text: any): ReactElement => (
    <div className="wrap-text-anywhere" style={{ paddingBottom: "15px" }}>
      {text}
    </div>
  );

  const renderLink = (text: string): ReactElement =>
    renderText(
      <a href={text} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    );

  const renderEmail = (text: string): ReactElement =>
    renderText(
      <a href={`mailto:${text}`} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    );

  const renderPeople = (text: string, email: string): ReactElement =>
    renderText(
      <a href={`mailto:${email}`} target="_blank" rel="noopener noreferrer">
        {text} <MailOutlined />
      </a>
    );

  const renderPhone = (text: string): ReactElement =>
    renderText(
      <a href={`tel:${text}`} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    );

  // ---------------------- ADD HANDLERS ----------------------
  const showModal = () => {
    setIsModalOpen(true);
    resetDeleteState()
  };
  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    resetDeleteState();
  };

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        const payload: CommunityAdditionalContact = {
          header: values.label,
          contactDetail: values.value,
          contactType: values.type,
          communityId: Number(applicationStore.SelectedAddress.communityId),
          id: 0,
          sortOrder: 0,
          userId: applicationStore.SelectedAddress.userId,
        };

        setIsLoading(true);

        const response = await contactsAndLinksStore.addCommunityAdditionalContact(payload);
        if (response.status === 200) {
          message.success("Record added successfully");
          setIsModalOpen(false);
          form.resetFields();
          refreshData();
        } else {
          setIsLoading(false);
          message.error("Failed to add Record");
        }
      })
      .catch((info) => console.log("Validation failed:", info));
  };


  // ---------------------- EDIT HANDLERS ----------------------
  const showEditModal = () => {


     if (!contactsAndLinks?.AdditionalContacts?.length) {
      message.warning("No contacts available to update");
      return;
    }

    resetDeleteState();
    const items = contactsAndLinks?.AdditionalContacts ?? [];
    setEditList(items.map((it) => ({ ...it })));
    setIsEditModalOpen(true);
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
     const items = contactsAndLinks?.AdditionalContacts ?? [];
    setEditList(items.map((it) => ({ ...it })));
    resetDeleteState();
    setFieldErrors({});
  };

  const handleEditSave = async () => {

    let errors: any = {};
    let hasError = false;
    editList.forEach((item, index) => {
      let itemErrors: any = {};

      if (!item.contactType || typeof item.contactType !== 'string' || !item.contactType.trim()) {
        itemErrors.contactType = "Contact type cannot be empty";
        hasError = true;
      }

      if (!item.header || typeof item.header !== 'string' || !item.header.trim()) {
        itemErrors.header = "Label cannot be empty";
        hasError = true;
      }

      if (!item.contactDetail || typeof item.contactDetail !== 'string' || !item.contactDetail.trim()) {
        itemErrors.contactDetail = "Value cannot be empty";
        hasError = true;
      }
      const errorMessage = validateContactDetail(item.contactType, item.contactDetail);
      if (errorMessage) {
        itemErrors.contactDetail = errorMessage;
        hasError = true;
      }

      if (Object.keys(itemErrors).length > 0) {
        errors[index] = itemErrors;
      }
    });

    if (hasError) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});


    const payload: any[] = editList.map((item: any, i: any) => ({
      header: item.header ?? item.label,
      contactDetail: item.contactDetail ?? item.value,
      contactType: item.contactType ?? item.type,
      communityId: item.communityId ?? applicationStore.SelectedAddress.communityId,
      id: item.id ?? 0,
      sortOrder: item.sortOrder ?? 0,
      userId: item.userId ?? applicationStore.SelectedAddress.userId
    }));



    setIsLoading(true);

    const response = await contactsAndLinksStore.updateCommunityAdditionalContact(payload);

    if (response.status === 200) {
      message.success("Record updated successfully");
      setIsEditModalOpen(false);
      refreshData();
    } else {
      setIsLoading(false);
      message.error("Failed to update record");
    }
  };

  const resetDeleteState = () => {
    setSelectedIds([]);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const reordered = Array.from(editList);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    const updated = reordered.map((item, index) => ({
      ...item,
      sortOrder: index + 1, // sortOrder starts at 1
    }));

    setEditList(updated);
  };

  // ---------------------- DELETE HANDLERS ----------------------


  const handleCheckboxChange = (id: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const showDeleteModal = () => {
    if (!contactsAndLinks?.AdditionalContacts?.length) {
      message.warning("No contacts available to delete");
      return;
    }
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      message.warning("Please select at least one contact to delete");
      return;
    }

    const selectedContacts =
      contactsAndLinks?.AdditionalContacts?.filter((item) =>
        selectedIds.includes(item.id)
      ) || [];




    Modal.confirm({
      title: "Are you sure ?",
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
            {selectedContacts.map((contact) => (
              <li key={contact.id}>
                <b>{contact.header}:</b> {contact.contactDetail}
              </li>
            ))}
          </ul>
          <p style={{ marginTop: "12px", fontWeight: "bold" }}>
            Note: This action cannot be undone.
          </p>
        </>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setIsLoading(true);

        const response = await contactsAndLinksStore.deleteCommunityAdditionalContact(selectedContacts);
        if (response.status === 200) {
          message.success("Record deleted successfully");
          setIsModalOpen(false);
          form.resetFields();
          handleDeleteCancel();
          setSelectedIds([]);
          refreshData();
        } else {
          message.error("Failed to delete record");
        }
      },
      onCancel: () => {
        resetDeleteState();
      },
    });

  };

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(contactsAndLinks?.AdditionalContacts?.map((c) => c.id) || []);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setSelectedIds([]);
  };

  // ---------------------- REFRESH ----------------------
  const refreshData = async () => {
    setIsLoading(true);
    const res = await contactsAndLinksStore.getContactsAndLinks();
    setContactsAndLinks(res);
    setIsLoading(false);
  };

  return (
    <Spin spinning={isLoading}>
      <Flex
        justify="space-between"
        wrap="wrap"
        align="center"
        gap="16px"
        style={{ paddingBottom: 24 }}
      >
        <h1 className="page-title">Contacts And Links</h1>
      </Flex>
      <Card
        key={"ContactsAndLinks"}
        extra={

          applicationStore.SelectedAddress?.role === 1 && (
            <Space>
              <>
                <Button className="icon-btn" onClick={showDeleteModal}>
                  <img src={DeleteIcon} alt="delete button" />
                </Button>
                <Button className="icon-btn" onClick={showEditModal}>
                  <img src={EditIcon} alt="edit button" />
                </Button>
                <Button type="primary" onClick={showModal}>
                  Add New
                </Button>
              </>
            </Space>
          )
        }
      >
        <Descriptions
          title={applicationStore.SelectedAddress.name}
          layout={"vertical"}
        >
          {contactsAndLinks?.ManagerName && (
            <Descriptions.Item label="Community Manager">
              {renderPeople(
                contactsAndLinks.ManagerName ?? "",
                contactsAndLinks.ManagerEmail ?? ""
              )}
            </Descriptions.Item>
          )}

          {contactsAndLinks?.WebSite && (
            <Descriptions.Item label="Community Website"> 
              {renderLink(contactsAndLinks?.WebSite)}
            </Descriptions.Item>
          )}

          {contactsAndLinks?.AdditionalContacts?.map((v) => {
            let text = renderText(v.contactDetail);
            if (v.contactType === "email") text = renderEmail(v.contactDetail);
            if (v.contactType === "phone") text = renderPhone(v.contactDetail);

            return (
              <Descriptions.Item key={v.id} label={v.header} >
                {text}
              </Descriptions.Item>
            );
          })}
        </Descriptions>
      </Card>

      {/* ADD MODAL */}
      <Modal
        title="Add Contact and Links"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Add"
        cancelText="Cancel"
      >

        <Spin spinning={isLoading}>
        <Form form={form} layout="vertical">
          <Form.Item
            label="Label"
            name="label"
            rules={[{ required: true, message: "Please enter label" }]}
          >
            <Input placeholder="Enter label" />
          </Form.Item>
          <Form.Item
            label="Value"
            name="value"
            dependencies={["type"]}
            rules={[
              { required: true, message: "Please enter value" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const type = getFieldValue("type");
                  const errorMessage = validateContactDetail(type, value);

                  if (errorMessage) {
                    return Promise.reject(new Error(errorMessage));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input placeholder="Enter your value" />
          </Form.Item>
          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: "Please select type" }]}
          >
            <Select placeholder="Select a type">
              <Option value="email">Email</Option>
              <Option value="phone">Phone</Option>
              <Option value="url">URL</Option>
            </Select>
          </Form.Item>
        </Form>
        </Spin>
      </Modal>


      {/* EDIt MODAL */}
      <Modal
        title="Edit Contacts and Links"
        open={isEditModalOpen}
        onCancel={handleEditCancel}
        onOk={handleEditSave}
        okText="Update"
        cancelText="Cancel"
        width={700}
        className="contact-edit-modal"
      >
         <Spin spinning={isLoading}>
        <div
          className="edit-modal-header"
        >
          <div></div>
          <div>Label</div>
          <div>Value</div>
          <div>Contact Type</div>
        </div>

        <div>
          {editList.map((item, index) => (
            <div
              key={item.id}
              draggable
              onDragStart={() => (dragItem.current = index)}
              onDragEnter={() => (dragOverItem.current = index)}
              onDragOver={(e) => e.preventDefault()}
              onDragEnd={handleSort}
              className="people-card"
            >
              <MenuOutlined style={{ cursor: "grab" }} />
              {/* Label Input */}
              <div>
                <Input
                  value={item.header}
                  onChange={(e) => {
                    handleChange(index, "header", e.target.value);
                    if (fieldErrors[index]?.header) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        [index]: { ...prev[index], header: undefined },
                      }));
                    }
                  }}
                />
                {fieldErrors[index]?.header && (
                  <div style={{ color: "red", fontSize: "12px", minHeight: "16px" }}>
                    {fieldErrors[index].header}
                  </div>
                )}
              </div>

              {/* Value Input */}
              <div>
                <Input
                  value={item.contactDetail}
                  onChange={(e) => {
                    handleChange(index, "contactDetail", e.target.value);
                    if (fieldErrors[index]?.contactDetail) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        [index]: { ...prev[index], contactDetail: undefined },
                      }));
                    }
                  }}
                />
                {fieldErrors[index]?.contactDetail && (
                  <div style={{ color: "red", fontSize: "12px", minHeight: "16px" }}>
                    {fieldErrors[index].contactDetail}
                  </div>
                )}
              </div>

              {/* Contact Type Select */}
              <div>
                <Select
                  value={item.contactType}
                  onChange={(value) => {
                    handleChange(index, "contactType", value);
                    if (fieldErrors[index]?.contactType) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        [index]: { ...prev[index], contactType: undefined },
                      }));
                    }
                  }}
                  style={{ width: "100%" }}
                  options={[
                    { value: "phone", label: "Phone" },
                    { value: "email", label: "Email" },
                    { value: "url", label: "URL" },
                  ]}
                />
                {fieldErrors[index]?.contactType && (
                  <div style={{ color: "red", fontSize: "12px", minHeight: "16px" }}>
                    {fieldErrors[index].contactType}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        </Spin>
      </Modal>

      {/* Delete MODAL */}
      <Modal
        title="Delete Contact and Links"
        open={isDeleteModalOpen}
        onOk={handleDelete}
        onCancel={handleDeleteCancel}
        okText="Proceed"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: "12px" }}>
          <span>
            Check the boxes next to the fields you want to delete.
          </span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ padding: "5px", textAlign: "left" }}>
                <Checkbox
                  checked={
                    selectedIds.length ===
                    (contactsAndLinks?.AdditionalContacts?.length || 0) &&
                    selectedIds.length > 0
                  }
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < (contactsAndLinks?.AdditionalContacts?.length || 0)
                  }
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
              </th>
              <th style={{ textAlign: "left", padding: "5px" }}>Label</th>
              <th style={{ textAlign: "left", padding: "5px" }}>Value</th>
            </tr>
          </thead>
          <tbody>
            {contactsAndLinks?.AdditionalContacts?.map((contact) => (
              <tr key={contact.id}>
                <td style={{ padding: "5px" }}>
                  <Checkbox
                    checked={selectedIds.includes(contact.id)}
                    onChange={(e) =>
                      handleCheckboxChange(contact.id, e.target.checked)
                    }
                  />
                </td>
                <td   className="modal-content-text"   style={{ padding: "5px" }}>{contact.header}</td>
                <td   className="modal-content-text"  style={{ padding: "5px" }}>{contact.contactDetail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </Spin>
  );
});

export default ContactsAndLinks;
